import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Compass, ArrowRight } from "lucide-react";

export default function PlanFinder() {
  const [need, setNeed] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const find = async () => {
    if (need.trim().length < 5) return toast.error("Tell us a little more about what you need");
    setBusy(true);
    try {
      const { data } = await api.post("/compare/recommend", { need: need.trim() });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't find a plan right now");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="plan-finder" className="border border-foreground bg-accent/20 p-8 lg:p-10">
      <div className="font-mono-label text-primary mb-3 flex items-center gap-2"><Compass className="w-4 h-4" /> Plan finder</div>
      <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight mb-3">Not sure which plan fits?</h2>
      <p className="text-muted-foreground mb-6 max-w-xl">Describe what you'd use DocuMind for, like how many documents, who uses it, and whether you need sharing or an API.</p>
      <Textarea data-testid="plan-finder-input" value={need} onChange={(e) => setNeed(e.target.value)} maxLength={600} rows={3}
        placeholder="e.g. I'm a PhD student with around 60 papers and I want to compare them side by side"
        className="rounded-sm bg-background mb-4" />
      <Button data-testid="plan-finder-submit" onClick={find} disabled={busy} className="rounded-sm bg-foreground text-background hover:bg-primary btn-invert">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Find my plan →"}
      </Button>
      {result && (
        <div data-testid="plan-finder-result" className="mt-8 border-t border-border pt-6 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="font-mono-label text-muted-foreground mb-1">We recommend</div>
            <div className="font-serif-display text-4xl" data-testid="plan-finder-plan">{result.name}</div>
            <div className="text-sm text-muted-foreground mt-1">From {result.price_from} · {Math.round(result.confidence * 100)}% match</div>
          </div>
          <Link to="/#pricing" data-testid="plan-finder-cta">
            <Button className="rounded-sm bg-primary text-primary-foreground hover:bg-foreground btn-invert">See {result.name} <ArrowRight className="ml-2 w-4 h-4" /></Button>
          </Link>
        </div>
      )}
    </section>
  );
}
